<template>
  <div :id="chartId || id" class="bar-graph-wrapper" :style="style" />
</template>

<script lang="ts">
import { PropType } from 'vue'
import Component from 'vue-class-component'

import BarGraph from '@/components/public/Charts/C3/BarGraph.vue'
import {
  ComparisonGraphSettingsConfig,
  ComparisonGraphCategory,
  FieldTypes,
  isFieldNumeric,
} from '@/types'
import { iterateShades, ShadeIterator } from '@/helpers/shadeIterator'

const props = {
  preview: {
    type: Object as PropType<ComparisonGraphSettingsConfig>,
    default: null,
  },
}

// eslint-disable-next-line no-use-before-define
@Component<ComparisonGraph>({
  watch: {
    rotateGraph() {
      this.redraw()
    },
    multilineXLabels() {
      this.redraw()
    },
    showXAxis() {
      this.redraw()
    },
    showYAxis() {
      this.redraw()
    },
    showLegend() {
      this.redraw()
    },
    legenditems(newValue, oldValue) {
      if (newValue.length !== oldValue.length) {
        this.redraw()
      }
    },
  },
})
export default class ComparisonGraph extends BarGraph.extend({ props }) {
  chart: any
  generateChart: any

  id = 'comparison-bar-graph'
  resizeObserver: ResizeObserver | null = null

  async mounted() {
    this.resizeObserver = new ResizeObserver(this.handleResize)
    this.resizeObserver.observe(this.$el as HTMLElement)
    this.handleResize()
  }

  beforeDestroy() {
    this.resizeObserver && this.resizeObserver.disconnect()
  }

  handleResize() {
    this.$nextTick(() => {
      if (this.chart) {
        this.chart.resize()
      }
    })
  }

  get config() {
    return this.$typedStore.activeVisualisation.visualisationStore.app.config
  }

  get useStoryColour() {
    return this.config.comparisonGraphSettings.useStoryColour
  }

  get formatValueByColumn() {
    return this.$typedStore.visualisationData.formatValueByColumn
  }

  get fieldValueForColumn() {
    return this.$typedStore.visualisationData.fieldValueForColumn
  }

  get field() {
    return this.$typedStore.visualisationApp.field
  }

  get monetaryFormatting() {
    return this.$typedStore.visualisationApp.monetaryFormatting
  }

  get numericFormatting() {
    return this.$typedStore.visualisationApp.numericFormatting
  }

  get fieldMap() {
    return this.config.fieldMap
  }

  get sidebarBotFontColor() {
    return this.config.storySettings.sidebarBot.fontColor
  }

  get originalColours() {
    return this.$typedStore.primary.app.config.appConfig?.originalTabColours ?? false
  }

  get storyColour() {
    return this.originalColours
      ? this.$typedStore.activeVisualisation.visualisationStore.entities.nodes.colour(
          this.$typedStore.activeVisualisation.storyPath.level1,
        )
      : this.$typedStore.public.display.activeTab?.colour ?? ''
  }

  get sidebarBotFontFamily() {
    return this.config.storySettings.sidebarBot.fontFamily
  }

  get sidebarBotFontSize() {
    return this.config.storySettings.sidebarBot.fontSize
  }

  get settings() {
    return {
      ...this.config.comparisonGraphSettings,
      ...this.preview,
    }
  }

  get rotateGraph() {
    return this.settings.rotate
  }

  get showXAxis() {
    return this.settings.showXAxis
  }

  get multilineXLabels() {
    return this.settings.multilineXLabels
  }

  get showYAxis() {
    return this.settings.showYAxis
  }

  get showLegend() {
    return this.settings.showLegend
  }

  get categories() {
    return this.settings.categories
  }

  get legenditems() {
    return this.settings.legenditems
  }

  get style() {
    return {
      '--font-scale': Number(this.sidebarBotFontSize) / 100,
      '--font-family': this.sidebarBotFontFamily,
    }
  }

  get yAxisCategories() {
    return this.legenditems.map((legenditem, index) => {
      return {
        ...legenditem,
        fields: this.categories.map(({ fields }) => fields[index]),
      }
    })
  }

  get fields() {
    return this.categories.reduce((acc: string[], { fields }) => acc.concat(fields), [])
  }

  get fieldsValue() {
    return this.fields.reduce((output: { [key: string]: number }, field) => {
      output[field] = this.fieldValueForColumn(field)
      return output
    }, {})
  }

  get c3json() {
    return this.categories.map(({ title, fields }: ComparisonGraphCategory) => {
      const cols = fields.reduce((output: { [key: string]: number }, field, index) => {
        output[this.yAxisCategories[index].label] = this.fieldsValue[field]
        return output
      }, {})
      return {
        title,
        ...cols,
      }
    })
  }

  get c3keys() {
    return {
      x: 'title',
      value: this.yAxisCategories.map(({ label }) => label),
    }
  }

  get c3colors() {
    let shades: ShadeIterator | null = null
    if (this.useStoryColour && this.storyColour) {
      shades = iterateShades(this.storyColour, this.yAxisCategories.length)
    }
    return this.yAxisCategories.reduce((output: { [key: string]: string }, { label, color }) => {
      output[label] = shades ? shades.next() : color
      return output
    }, {})
  }

  get c3axis() {
    return {
      rotated: this.rotateGraph,
      x: {
        show: this.showXAxis,
        tick: {
          centered: true,
          multiline: this.multilineXLabels,
        },
        type: 'category',
      },
      y: {
        show: this.showYAxis,
        tick: {
          format: (d: number) => `${this.formatValueByUnit(d)}`,
        },
      },
    }
  }

  get c3legend() {
    return {
      show: this.showLegend,
    }
  }

  get c3tooltip() {
    return {
      format: {
        name: (name: string, ratio: number | undefined, id: string, index: number) => {
          const field = this.getField(id, index)
          return field ? this.field(field)?.name : ''
        },
        value: (value: number, ratio: number | undefined, id: string, index: number) => {
          const field = this.getField(id, index)
          return field ? this.formatValueByColumn(field, value) : value
        },
      },
    }
  }

  get uniqueCategories() {
    return Array.from(new Set(this.fields))
  }

  get fieldMapByCategories() {
    return this.fieldMap.filter(({ column }) => this.uniqueCategories.includes(column))
  }

  get uniqueNumericUnits() {
    return Array.from(
      new Set(this.fieldMapByCategories.filter(isFieldNumeric).map(({ unit }) => unit)),
    )
  }

  formatValueByUnit(value: number) {
    if (this.fieldMapByCategories.every(({ type }) => type === FieldTypes.MONETARY)) {
      return this.monetaryFormatting(value)
    }
    if (
      this.fieldMapByCategories.every(({ type }) => type === FieldTypes.NUMERIC) &&
      this.uniqueNumericUnits.length === 1
    ) {
      const formatted = this.numericFormatting(value)
      const unit = this.uniqueNumericUnits[0] ? ` ${this.uniqueNumericUnits[0]}` : ''
      return formatted + unit
    }
    return value
  }

  redraw() {
    this.chart?.destroy()
    this.chart = this.generateChart()
  }

  getField(id: string, index: number) {
    const category = this.yAxisCategories.find(({ label }: { label: string }) => label === id)
    return category ? category.fields[index] : null
  }
}
</script>

<style>
.c3-tooltip {
  color: black;
}
.bar-graph-wrapper .domain {
  stroke: var(--font-color);
}
.bar-graph-wrapper .c3-axis-x line {
  stroke: var(--font-color);
}
.bar-graph-wrapper .c3-axis-y line {
  stroke: var(--font-color);
}
.bar-graph-wrapper .c3-axis-x text tspan {
  fill: var(--font-color);
  font-family: var(--font-family);
}
.bar-graph-wrapper .c3-axis-y text tspan {
  fill: var(--font-color);
  font-family: var(--font-family);
}
.bar-graph-wrapper .c3-legend-item text {
  fill: var(--font-color);
  font-family: var(--font-family);
}

.c3-target-mimic-invisibility {
  opacity: 0 !important;
}
</style>
